import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Paper, Stack, TextField, Typography, Chip } from '@mui/material';
import { client } from '../../api/Api';

function useConsole() {
  const [lines, setLines] = useState([]);
  const append = useCallback((type, message, payload) => {
    const time = new Date().toLocaleTimeString();
    const entry = { id: Date.now() + Math.random(), time, type, message, payload };
    setLines((prev) => [entry, ...prev].slice(0, 500));
  }, []);
  const apiLog = useCallback(async (fn, description) => {
    append('info', `▶ ${description}…`);
    try {
      const res = await fn();
      append('success', `✓ ${description} OK`, res?.data);
      return res?.data;
    } catch (e) {
      append('error', `✗ ${description} FAILED`, {
        message: e?.message,
        status: e?.response?.status,
        data: e?.response?.data,
      });
      throw e;
    }
  }, [append]);
  const clear = useCallback(() => setLines([]), []);
  return { lines, append, apiLog, clear };
}

const Line = ({ line }) => {
  const color = line.type === 'error' ? '#ef4444' : line.type === 'success' ? '#22c55e' : line.type === 'warn' ? '#f59e0b' : '#e5e7eb';
  return (
    <div style={{ padding: '6px 10px', borderBottom: '1px solid #111827' }}>
      <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 500 }}>{line.time}</div>
      <div style={{ color, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12, fontWeight: 600 }}>{line.message}</div>
      {line.payload !== undefined && (
        <pre style={{ marginTop: 6, background: '#0a0a0a', color: '#e5e7eb', borderRadius: 6, padding: 10, overflowX: 'auto', border: '1px solid #1f2937', fontFamily: '"Noto Sans Mono", ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace', fontSize: 12, lineHeight: 1.35 }}>
          {JSON.stringify(line.payload, null, 2)}
        </pre>
      )}
    </div>
  );
};

export default function AdminNormalizedProducts() {
  const [idsInput, setIdsInput] = useState('');
  const [productId, setProductId] = useState('');
  const { lines, apiLog, clear, append } = useConsole();
  const [connected, setConnected] = useState(false);
  const byIds = useMemo(() => idsInput.split(',').map(s => s.trim()).filter(Boolean).map(Number).filter(n => !isNaN(n)), [idsInput]);

  useEffect(() => {
    // Subscribe to server logs via SSE
    const isLocalhost = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
    const sseUrl = isLocalhost ? 'http://localhost:8080/api/admin/normalized/logs' : '/api/admin/normalized/logs';
    const es = new EventSource(sseUrl);

    const handle = (type) => (ev) => {
      const raw = ev && typeof ev.data !== 'undefined' ? ev.data : undefined;
      const t = type === 'message' ? 'info' : (type || 'info');

      // Skip empty/undefined messages to avoid showing "[SERVER] undefined"
      if (raw === undefined || raw === null || raw === '' || raw === 'undefined' || raw === 'null') {
        return;
      }

      // Try to parse JSON payloads and extract a readable message
      try {
        const obj = typeof raw === 'string' ? JSON.parse(raw) : raw;
        const msg = (obj && (obj.message || obj.msg || obj.text)) || raw;
        append(t, `[SERVER] ${msg}`, obj);
      } catch (e) {
        // Not JSON, just print the raw text
        append(t, `[SERVER] ${raw}`);
      }
    };

    es.onopen = () => { setConnected(true); append('info', '[SERVER] Live log connection established'); };
    es.onerror = () => { if (connected) append('warn', '[SERVER] Live log connection lost'); setConnected(false); };
    es.onmessage = handle('message');
    es.addEventListener('info', handle('info'));
    es.addEventListener('warn', handle('warn'));
    es.addEventListener('error', handle('error'));
    es.addEventListener('debug', handle('debug'));

    return () => {
      try { es.close(); } catch {}
      setConnected(false);
    };
  }, [append]);

  const runMissing = () => apiLog(
    () => client.post('/admin/normalized/run'),
    'Paleisti normalizaciją trūkstamiems produktams'
  );

  const runByIds = () => apiLog(
    () => client.post('/admin/normalized/by-ids', byIds),
    `Normalizuoti pagal ID sąrašą (${byIds.join(', ') || '—'})`
  );

  const getByProduct = () => apiLog(
    () => client.get(`/admin/normalized/${productId}`),
    `Gauti normalizuotą įrašą produktuI (${productId})`
  );

  const normalizeOne = () => apiLog(
    () => client.post(`/admin/normalized/one/${productId}`),
    `Normalizuoti vieną produktą (${productId})`
  );

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', display: 'grid', gap: 2 }}>
      <Typography variant="h4" sx={{ mb: 1 }}>Normalized Products</Typography>

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Veiksmai</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <Button onClick={runMissing} variant="contained">Paleisti trūkstamus</Button>

          <TextField
            label="ID sąrašas (kableliais)"
            placeholder="12, 34, 56"
            value={idsInput}
            onChange={(e) => setIdsInput(e.target.value)}
            size="small"
            sx={{ minWidth: 260 }}
          />
          <Button onClick={runByIds} variant="outlined">Paleisti pagal ID</Button>
        </Stack>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Vienas produktas</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <TextField
            label="Produkto ID"
            placeholder="123"
            value={productId}
            onChange={(e) => setProductId(e.target.value)}
            size="small"
            sx={{ minWidth: 180 }}
          />
          <Button onClick={getByProduct} variant="outlined">Gauti normalizuotą</Button>
          <Button onClick={normalizeOne} variant="contained">Normalizuoti dabar</Button>
        </Stack>
      </Paper>

      <Paper sx={{ p: 0, overflow: 'hidden', bgcolor: '#000', color: '#e5e7eb', border: '1px solid #111827', fontSize: 11 }} style={{ fontFamily: 'Noto Sans, Roboto, Arial, Helvetica, "Segoe UI", system-ui, -apple-system, sans-serif' }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" px={2} py={1.2} borderBottom="1px solid #111827" sx={{ bgcolor: '#0a0a0a' }}>
          <Typography variant="h6" sx={{ fontSize: 12, color: '#e5e7eb' }}>Konsolė</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip size="small" label={connected ? 'Live logs: connected' : 'Live logs: disconnected'} color={connected ? 'success' : 'default'} />
            <Button size="small" onClick={clear} variant="outlined" sx={{ borderColor: '#374151', color: '#e5e7eb', '&:hover': { borderColor: '#4b5563' } }}>Išvalyti</Button>
          </Stack>
        </Box>
        <div style={{ maxHeight: 420, overflow: 'auto' }}>
          {lines.length === 0 ? (
            <div style={{ padding: 12, color: '#9ca3af', fontSize: 11 }}>Veiksmų dar nėra. Paleiskite vieną iš mygtukų.</div>
          ) : (
            lines.map((l) => <Line key={l.id} line={l} />)
          )}
        </div>
      </Paper>
    </Box>
  );
}
